<script>
import Layout from '@layouts/main';
import QuoteForm from '@views/quotes/form';
import QuotePositions from '@views/quotes/positions/positions';
import beforeLeaveAfterEdit from '@mixins/beforeLeaveAfterEdit';
import cloneDeep from 'lodash.clonedeep';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import '@src/plugins/websockets';
import utility from '@mixins/utility';
import QuoteModel from '@models/Quote';
import AdminMultiselect from '@/src/components/AdminMultiselect';
import AdminModel from '@models/Admin';

export default {
  name: 'QuoteEdit',
  components: {
    Layout,
    QuoteForm,
    QuotePositions,
    FontAwesomeIcon,
    AdminMultiselect,
  },
  mixins: [beforeLeaveAfterEdit, utility],
  props: {
    quote: {
      type: Object,
      required: true,
      default: null,
    },
    quotePositions: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  page() {
    return {
      title: `${this.$t('titles.edit_quote')} #${this.$route.params.id}`,
    };
  },
  data() {
    return {
      adminModel: AdminModel,
      showReleaseForModal: false,
      notCollapsed: {
        quote_positions: false,
      },
      uniqueKey: Math.random().toString(36).substr(2, 9),
      breadcrumbItems: [
        {
          text: this.$t('titles.home'),
          to: { name: 'home.index' },
        },
        {
          text: this.$t('titles.quotes'),
          to: { name: 'quotes.index' },
        },
        {
          text: `${this.$t('titles.edit_quote')} #${this.$route.params.id}`,
          active: true,
        },
      ],
      quoteLocal: {
        id: null,
      },
      quotePositionsLocal: [],
    };
  },
  computed: {
    quotePositionsKey() {
      return this.uniqueKey;
    },
    quoteForPositions() {
      return this.quoteLocal;
    },
    quotePositionsForPositions() {
      return this.quotePositionsLocal;
    },
  },
  mounted() {
    // check later when there's time is there some better solution
    this.quotePositionsLocal = cloneDeep(this.quotePositions);

    this.quoteLocal = cloneDeep(this.quote);
    if ('create' in this.$route.query) {
      this.notCollapsed.quote_positions = true;
    }
  },
  methods: {
    refreshQuoteAndPositions() {
      if (this.refreshTimeout) {
        clearTimeout(this.refreshTimeout);
      }
      // Set a 4-second delay before updating quoteLocal
      // for total to calculate correctly
      this.refreshTimeout = setTimeout(() => {
        // pull fresh quote data
        QuoteModel.find(this.quoteLocal.id)
          .then((response) => {
            this.quoteLocal = cloneDeep(response.quote);
            this.quotePositionsLocal = cloneDeep(response.quote.quote_positions);
          });

        this.$nextTick(() => {
          this.setUnchanged();
        });
        this.refreshTimeout = null;
      }, 4000);
    },
    refreshQuoteTotal() {
      if (this.refreshTimeout) {
        clearTimeout(this.refreshTimeout);
      }
      // Set a 4-second delay before updating quote total
      // for total to calculate correctly (don't refresh positions, we already have fresh data)
      this.refreshTimeout = setTimeout(() => {
        // pull fresh quote data (only update the total)
        QuoteModel.find(this.quoteLocal.id)
          .then((response) => {
            this.quoteLocal = cloneDeep(response.quote);
          });

        this.$nextTick(() => {
          this.setUnchanged();
        });
        this.refreshTimeout = null;
      }, 4000);
    },
    update(updatedQuote) {
      if (updatedQuote) {
        this.quoteLocal = cloneDeep(updatedQuote);
      }
      this.refreshQuoteAndPositions();
    },
    addItemsToList(name, items) {
      items.forEach((sst) => {
        this[name].push(sst);
      });
      // Sort by order field to respect position ordering
      this[name].sort((a, b) => a.order - b.order);
      // refresh component
      this.uniqueKey = Math.random().toString(36).substr(2, 9);
    },
    addItemToList(name, item) {
      this[name].push(item);
      // refresh component
      this.uniqueKey = Math.random().toString(36).substr(2, 9);
      this.refreshQuoteTotal();
      // Reload version history
      if (this.$refs.quoteForm) {
        this.$refs.quoteForm.reloadVersions();
      }
    },
    updateItemInList(name, item, key) {
      this.$set(this[name], key, cloneDeep(item));
      this.refreshQuoteTotal();
      // Reload version history
      if (this.$refs.quoteForm) {
        this.$refs.quoteForm.reloadVersions();
      }
    },
    deleteItemFromList(name, id, key) {
      this[name].splice(key, 1);
      this.refreshQuoteTotal();
      // Reload version history
      if (this.$refs.quoteForm) {
        this.$refs.quoteForm.reloadVersions();
      }
    },
    deletedMultiple(name, keys) {
      keys.forEach((index, key) => {
        this[name].splice(index - key, 1);
      });
      this.refreshQuoteTotal();
      // Reload version history
      if (this.$refs.quoteForm) {
        this.$refs.quoteForm.reloadVersions();
      }
    },
    updatedMultiple(name, scheduledServiceTimes, keys) {
      keys.forEach((key, index) => {
        // bug fix for table
        scheduledServiceTimes[index]._showDetails = false;
        this.$set(this[name], key, scheduledServiceTimes[index]);
      });
      this.refreshQuoteAndPositions();
    },
    exportPdf() {
      const id = this.$route.params.id;
      this.$wait.start(`downloading-quote-${id}`);
      this.$http
          .get(`admin/quotes/${id}/export`, {
                params: { lang: this.quoteLocal.language },
                responseType: 'blob'
          })
          .then((response) => {
          this.$wait.end(`downloading-quote-${id}`);
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          const fileName = this.getFileNameFromContentDisposition(String(headers['content-disposition']));
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName !== '' ? fileName : `Quote #${id}.pdf`;
          link.click();
        })
        .catch(() => this.$wait.end(`downloading-quote-${id}`));
    },
    createJob() {
      // ask for confirmation first
      if(!confirm(this.$t('alerts.create_job_from_quote'))) {
        return;
      }

      const id = this.$route.params.id;
      this.$wait.start(`create-job-from-quote-${id}`);
      this.$http
        .post(`admin/quotes/${id}/create-job`, this.quoteLocal)
        .then((response) => {
          this.$wait.end(`create-job-from-quote-${id}`);
          const jobId = response.data.job.id;
          // Navigate to the newly created job in new tab
          window.open(this.$router.resolve({ name: 'jobs.edit', params: { id: jobId } }).href, '_blank');
        })
        .catch(() => {
          this.$toasted.error(this.$t('errors.create_job_from_quote'));
          this.$wait.end(`create-job-from-quote-${id}`);
        });
    },
    releaseQuote() {
      this.$nextTick(() => {
        this.$wait.start('releasing-quote');
        this.$http
          .put(`admin/quotes/${this.quoteLocal.id}/release`, {
            released_for_id: this.quoteLocal.released_for_id,
          })
          .then((response) => {
            this.$wait.end('releasing-quote');
            this.showReleaseForModal = false;
            this.quoteLocal = cloneDeep(response.data.quote);
          })
          .catch(() => {
            this.$wait.end('releasing-quote');
          });
      });
    }
  },
}
</script>

<template>
  <Layout>
    <b-breadcrumb :items="breadcrumbItems" />
    <b-card border-variant="dark" :header="$t('titles.edit_quote')" header-tag="header" header-bg-variant="dark" header-text-variant="white" class="mb-3">
      <QuoteForm ref="quoteForm" :loaded-id="$route.params.id" @submitted="update" @entity-changed="initBeforeLeave" />
    </b-card>

    <b-card v-if="quoteLocal.id" border-variant="dark" header-tag="header" header-bg-variant="dark" header-text-variant="white" class="mb-3">
      <template v-slot:header>
        <div>
          {{ $t('titles.quote_positions') }}
          <span class="fa-pull-right cursor-pointer" @click.stop="notCollapsed.quote_positions = !notCollapsed.quote_positions">
            <span>{{ $t('titles.new_quote_position') }}</span
            >&nbsp;
            <FontAwesomeIcon :icon="['fas', notCollapsed.quote_positions ? 'chevron-down' : 'chevron-right']" />
          </span>
        </div>
      </template>
      <QuotePositions
        :key="quotePositionsKey"
        ref="quotePositions"
        :quote="quoteForPositions"
        :quote-positions="quotePositionsForPositions"
        :collapsed.sync="notCollapsed.quote_positions"
        @submitted="addItemToList('quotePositionsLocal', ...arguments)"
        @duplicated="addItemsToList('quotePositionsLocal', ...arguments)"
        @updated="updateItemInList('quotePositionsLocal', ...arguments)"
        @deleted="deleteItemFromList('quotePositionsLocal', ...arguments)"
        @deleted-multiple="deletedMultiple('quotePositionsLocal', ...arguments)"
        @updated-multiple="updatedMultiple('quotePositionsLocal', ...arguments)"
      />
    </b-card>
    <b-card border-variant="dark" :header="$t('titles.processing')" header-tag="header" header-bg-variant="dark" header-text-variant="white" class="mb-3">
      <b-row>
        <b-col lg="3" sm="12" class="mb-3 mb-md-0">
          <b-button type="button"  variant="outline-dark" class="w-100" @click="exportPdf">
            {{ $t('actions.download') }}
            <FontAwesomeIcon v-show="$wait.is(`downloading-quote-${quote.id}`)" :icon="['fas', 'spinner']" pulse />
          </b-button>
        </b-col>
        <b-col lg="3" sm="12" class="mb-3 mb-md-0">
          <b-button type="button"  variant="outline-dark" class="w-100" @click="createJob">
            {{ $t('actions.create_job') }}
          </b-button>
        </b-col>
        <b-col lg="3" sm="12">
          <b-button type="button"  variant="outline-dark" class="w-100" @click="showReleaseForModal = true">
            {{ $t('actions.release') }}
          </b-button>
        </b-col>
      </b-row>
    </b-card>
    <b-modal v-if="showReleaseForModal" v-model="showReleaseForModal" size="lg" hide-footer :title="$t('titles.release')" static @hidden="showReleaseForModal = false">
      <AdminMultiselect
        v-model="quoteLocal.released_for_id"
        v-validate="''"
        data-vv-name="released_for_id"
        :data-vv-as="$t('fields.release_for')"
        :initial-value="quoteLocal.released_for"
        :state="errors.has('released_for_id')"
        :placeholder="$t('placeholders.release_for')"
        :model="adminModel"
        class="mb-3"
      >
      </AdminMultiselect>
      <b-button type="button"  variant="outline-dark" @click="releaseQuote">
        {{ $t('actions.submit') }}
      </b-button>
    </b-modal>
  </Layout>
</template>
